import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Clock, MapPin, Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ZoneDetail {
  id: string;
  name: string;
  zone_type: string;
  description: string | null;
  opening_hours: string | null;
  photo_url: string | null;
  practical_info: string | null;
}

interface Props {
  zone: ZoneDetail | null;
  isOpen: boolean;
  onClose: () => void;
  isLight?: boolean;
}

const ZONE_ICONS: Record<string, string> = {
  spa: '🧖', pool: '🏊', lobby: '🏨', terrace: '☀️', gym: '🏋️',
  conference: '📋', rooftop: '🌇', garden: '🌳', beach: '🏖️',
  reception: '🏕️', playground: '🎡', snack_bar: '🍔', laundry: '🧺',
  entertainment: '🎶', sports: '⚽', other: '📍', default: '📍',
};

const ZoneDetailDialog = ({ zone, isOpen, onClose, isLight = false }: Props) => {
  if (!zone) return null;

  const icon = ZONE_ICONS[zone.zone_type] || ZONE_ICONS.default;
  const hasContent = zone.description || zone.opening_hours || zone.photo_url || zone.practical_info;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`sm:max-w-md ${isLight ? 'bg-white' : 'bg-slate-900 border-slate-700 text-white'}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span className="text-2xl">{icon}</span>
            <span className={isLight ? 'text-gray-900' : 'text-white'}>{zone.name}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {zone.photo_url && (
            <img 
              src={zone.photo_url} 
              alt={zone.name} 
              className="w-full h-48 object-cover rounded-xl" 
            />
          )}

          {zone.description && (
            <p className={`text-sm ${isLight ? 'text-gray-600' : 'text-slate-300'}`}>
              {zone.description}
            </p>
          )}

          {zone.opening_hours && (
            <div className={`flex items-start gap-3 p-3 rounded-xl ${isLight ? 'bg-gray-50' : 'bg-slate-800'}`}>
              <Clock className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isLight ? 'text-gray-500' : 'text-slate-400'}`} />
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>Horaires</p>
                <p className={`text-sm whitespace-pre-line ${isLight ? 'text-gray-700' : 'text-slate-200'}`}>{zone.opening_hours}</p>
              </div>
            </div>
          )}

          {zone.practical_info && (
            <div className={`flex items-start gap-3 p-3 rounded-xl ${isLight ? 'bg-gray-50' : 'bg-slate-800'}`}>
              <Info className={`h-5 w-5 mt-0.5 flex-shrink-0 ${isLight ? 'text-gray-500' : 'text-slate-400'}`} />
              <div>
                <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>Infos pratiques</p>
                <p className={`text-sm whitespace-pre-line ${isLight ? 'text-gray-700' : 'text-slate-200'}`}>{zone.practical_info}</p>
              </div>
            </div>
          )}

          {!hasContent && (
            <p className={`text-sm text-center py-4 ${isLight ? 'text-gray-400' : 'text-slate-500'}`}>
              Aucune information disponible pour cet espace.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ZoneDetailDialog;
